import React, { useState, useEffect, useCallback } from "react";
import styles from "./DealerShipDetails.module.scss";
import { Button, Grid } from "@mui/material";
import {
  TextValidator,
  ValidatorForm,
} from "react-material-ui-form-validator";
import MultiToggleWidget from "../../../shared/toggles/multiToggleWidget/MultiToggleWidget";
import EditIcon from "../../../shared/Edit/EditIcon";
import ClearIcon from "@mui/icons-material/Clear";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../../common/HasPermissionTo";
import DealerDetailsClient from "../../../shared/clients/DealerDetailsClient";
import AppActionsLogClient from "../../../shared/clients/AppActionsLogClient";

function DealerEmployeeTable(props) {
  const currentDealer = props.dealers;

  const refreshNewEmployee = useCallback(() => {
    return {
      id: "",
      dealerId: props.dealers.completeDealerId,
      fullName: "",
      emailAddress: "",
      receiveEmailsForNotifications: true,
      receiveEmailsForPendingActions: true,
    };
  }, [props.dealers.completeDealerId]);

  const [employees, setEmployees] = useState([]);
  const [addNewRowActive, setAddNewRowActive] = useState(false);
  const [newEmployee, setNewEmployee] = useState(refreshNewEmployee);
  const [employeesToEdit, setEmployeesToEdit] = useState(new Map());
  const [employeeLimitReached, setEmployeeLimitReached] = useState(false);

  const getDealerEmployees = useCallback(() => {
    DealerDetailsClient.getDealerEmployees(
      props.user,
      currentDealer.completeDealerId
    )
      .then((employees) => {
        setEmployees(employees);
        setEmployeeLimitReached(employees.length > 9);
        props.disableLiveUpdateEmployees();
      })
      .catch(() => {});
  }, [props.user, currentDealer.completeDealerId, props]);

  const refreshEmployees = useCallback(() => {
    props.enableLiveUpdateEmployees();
  }, [props]);

  const onSelectEmailNotification = useCallback((employee, event) => {
    const selectedStatus = event.target.checked;

    DealerDetailsClient.updateDealerEmployeeEmailNotifications(
      props.user,
      employee,
      selectedStatus
    )
      .then(() => {
        setEmployees(prevEmployees => {
          const copy = [...prevEmployees];
          const index = prevEmployees.findIndex(emp => emp.id === employee.id);
          const employeeToChange = { ...copy[index] };
          employeeToChange.receiveEmailsForNotifications = selectedStatus;
          copy.splice(index, 1, employeeToChange);
          return copy;
        });
        resetRow(employee.id);
      })
      .then(() => {
        const logPayload = {
          notificationsEmailEnabled: selectedStatus,
          userId: props.user.userId,
        };
        AppActionsLogClient.recordEmailNotificationToggle(
          props.user,
          logPayload
        )
          .then(() => {})
          .catch(() => {});
      });
  }, [props.user]);

  const onSelectPendingActionNotification = useCallback((employee, event) => {
    const selectedStatus = event.target.checked;

    DealerDetailsClient.updateDealerEmployeePendingActionsEmails(
      props.user,
      employee,
      selectedStatus
    )
      .then(() => {
        setEmployees(prevEmployees => {
          const copy = [...prevEmployees];
          const index = prevEmployees.findIndex(emp => emp.id === employee.id);
          const employeeToChange = { ...copy[index] };
          employeeToChange.receiveEmailsForPendingActions = selectedStatus;
          copy.splice(index, 1, employeeToChange);
          return copy;
        });
        resetRow(employee.id);
      })
      .then(() => {
        const payload = {
          userId: props.user.userId,
          notificationsEmailEnabled: selectedStatus,
        };

        AppActionsLogClient.recordEmployeePendingActionsEmailsToggle(
          props.user,
          payload
        )
          .then(() => {})
          .catch(() => {});
      })
      .catch(() => {});
  }, [props.user]);

  const editExistingEmployee = useCallback((event) => {
    const editEmployee = employees.find(
      (employee) => employee.id === parseInt(event.currentTarget.value)
    );
    setEmployeesToEdit(prevMap => {
      const copy = new Map(prevMap);
      copy.set(editEmployee.id, editEmployee);
      return copy;
    });
  }, [employees]);

  const deleteExistingEmployee = useCallback((event) => {
    const employeeId = parseInt(event.currentTarget.value);
    DealerDetailsClient.deleteDealerEmployee(props.user, employeeId)
      .then(() => {
        resetRow(employeeId);
        setEmployees(prevEmployees => {
          const copy = [...prevEmployees];
          const index = prevEmployees.findIndex(emp => emp.id === employeeId);
          copy.splice(index, 1);
          setEmployeeLimitReached(copy.length > 9);
          return copy;
        });
      })
      .then(() => {
        const payload = {
          userId: props.user.userId,
        };

        AppActionsLogClient.recordDeleteEmployee(props.user, payload)
          .then(() => {})
          .catch(() => {});
      })
      .catch(() => {});
  }, [props.user]);

  const editEmployeeNameOnChange = useCallback((employeeId, event) => {
    const modifiedName = event.target.value;
    setEmployeesToEdit(prevMap => {
      const copy = new Map(prevMap);
      const newEmployee = {
        ...copy.get(employeeId),
        fullName: modifiedName,
      };
      copy.set(employeeId, newEmployee);
      return copy;
    });
  }, []);

  const editEmployeeEmailOnChange = useCallback((employeeId, event) => {
    const modifiedEmail = event.target.value;
    setEmployeesToEdit(prevMap => {
      const copy = new Map(prevMap);
      const newEmployee = {
        ...copy.get(employeeId),
        emailAddress: modifiedEmail,
      };
      copy.set(employeeId, newEmployee);
      return copy;
    });
  }, []);

  const updateEmployee = useCallback((employee) => {
    DealerDetailsClient.updateDealerEmployee(props.user, employee)
      .then((updateEmployee) => {
        setEmployees(prevEmployees => {
          const copy = [...prevEmployees];
          const index = prevEmployees.findIndex(emp => emp.id === updateEmployee.id);
          copy.splice(index, 1, updateEmployee);
          return copy;
        });
        resetRow(updateEmployee.id);
        refreshEmployees();
      })
      .then(() => {
        const payload = {
          userId: props.user.userId,
        };

        AppActionsLogClient.recordEditEmployee(props.user, payload)
          .then(() => {})
          .catch(() => {});
      });
  }, [props.user, refreshEmployees]);

  const cancelEdit = useCallback((event) => {
    resetRow(event.currentTarget.value);
  }, []);

  const resetRow = useCallback((employeeId) => {
    const empId = parseInt(employeeId);
    setEmployeesToEdit(prevMap => {
      if (prevMap.has(empId)) {
        const copy = new Map(prevMap);
        copy.delete(empId);
        return copy;
      }
      return prevMap;
    });
  }, []);

  const addNewEmployee = useCallback(() => {
    DealerDetailsClient.addEmployee(
      props.user,
      newEmployee,
      currentDealer.completeDealerId
    )
      .then((newEmployeeResponse) => {
        setEmployees(prevEmployees => [...prevEmployees, newEmployeeResponse]);
        setAddNewRowActive(false);
        setNewEmployee({
          id: "",
          dealerId: currentDealer.completeDealerId,
          fullName: "",
          emailAddress: "",
          receiveEmailsForNotifications: true,
          receiveEmailsForPendingActions: true,
        });
        refreshEmployees();
      })
      .then(() => {
        const payload = {
          userId: props.user.userId,
        };

        AppActionsLogClient.recordAddEmployee(props.user, payload)
          .then(() => {})
          .catch(() => {});
      })
      .catch(() => {});
  }, [props.user, newEmployee, currentDealer.completeDealerId, refreshEmployees]);

  const cancelNewEmployee = useCallback(() => {
    setAddNewRowActive(false);
    setNewEmployee(refreshNewEmployee());
  }, [refreshNewEmployee]);

  const showNewEmployeeRow = useCallback(() => {
    setAddNewRowActive(true);
  }, []);

  const newEmployeeEmailOnChange = useCallback((event) => {
    setNewEmployee(prev => ({
      ...prev,
      emailAddress: event.target.value,
    }));
  }, []);

  const newEmployeeNameOnChange = useCallback((event) => {
    setNewEmployee(prev => ({
      ...prev,
      fullName: event.target.value,
    }));
  }, []);

  useEffect(() => {
    getDealerEmployees();
  }, [getDealerEmployees]);

  useEffect(() => {
    if (props.renderAgain) {
      getDealerEmployees();
    }
  }, [props.renderAgain, getDealerEmployees]);

  return (
    <>
      <div className={styles.employeeEmailHeader}>
        <div className={styles.sectionHeaderEmployees}>
          {props.t("DealerShipDetails.employeeHeader")}
        </div>
        {employees.length > 0 && (
          <>
            <Grid container className={styles.subHeader}>
              <Grid item md={3} className={styles.employeeInfoHeader}>
                {props.t("DealerShipDetails.employees")}
              </Grid>
              <Grid item md={3} className={styles.employeeInfoHeader}>
                {props.t("DealerShipDetails.employeesEmail")}
              </Grid>
              <Grid item md={2} className={styles.notifications}>
                <div>
                  {props.t("DealerShipDetails.employeesNotifications")}
                </div>
              </Grid>

              <Grid item md={2} className={styles.notifications}>
                <HasPermissionTo
                  perform={["receivePendingActionColumn"]}
                  permissions={props.user.permissions.rolePermissions}
                  render={() => (
                    <div>
                      {props.t(
                        "DealerShipDetails.employeesPendingActions"
                      )}
                    </div>
                  )}
                />
              </Grid>
              <Grid item md={2} className={styles.employeeInfoHeader}>
                &nbsp;
              </Grid>
            </Grid>

            {employees.map((employee, index) => (
              <div key={`employee_${index}`}>
                {employeesToEdit.has(employee.id) ? (
                  <ValidatorForm
                    onSubmit={() =>
                      updateEmployee(employeesToEdit.get(employee.id))
                    }
                  >
                    <Grid
                      container
                      className={`${styles.tableBody} ${styles.employeeInfo}`}
                    >
                      <Grid item md={3} className={styles.employeeName}>
                        <TextValidator
                          name={`fullName_${employee.id}`}
                          style={{ width: "70%" }}
                          value={employeesToEdit.get(employee.id).fullName}
                          variant="outlined"
                          validators={["required"]}
                          onChange={(e) =>
                            editEmployeeNameOnChange(employee.id, e)
                          }
                          errorMessages={[
                            props.t("Error.requiredField"),
                          ]}
                        />
                      </Grid>
                      <Grid item md={3} className={styles.employeeEmail}>
                        <TextValidator
                          name={`email_${employee.id}`}
                          style={{ width: "70%" }}
                          value={employeesToEdit.get(employee.id).emailAddress}
                          variant="outlined"
                          validators={["required", "isEmail"]}
                          onChange={(e) =>
                            editEmployeeEmailOnChange(employee.id, e)
                          }
                          errorMessages={[
                            props.t("Error.requiredField"),
                            props.t("Error.invalidInput"),
                          ]}
                        />
                      </Grid>
                      <Grid item className={styles.employeeSwitches} md={2}>
                        {employee.receiveEmailsForNotifications
                          ? props.t("DealerShipDetails.yes")
                          : props.t("DealerShipDetails.no")}
                      </Grid>
                      <HasPermissionTo
                        perform={["receivePendingActionColumn"]}
                        permissions={
                          props.user.permissions.rolePermissions
                        }
                        render={() => (
                          <Grid
                            item
                            className={styles.employeeSwitches}
                            md={2}
                          >
                            {employee.receiveEmailsForPendingActions
                              ? props.t("DealerShipDetails.yes")
                              : props.t("DealerShipDetails.no")}
                          </Grid>
                        )}
                        noRender={() => <Grid item md={2} />}
                      />
                      <Grid item md={2} className={styles.editDiv}>
                        <Button
                          id={`updateEmployee_${employee.id}`}
                          data-testid={`updateEmployee_${employee.id}`}
                          value={employee.id}
                          type="submit"
                        >
                          <div className={styles.confirm}>OK</div>
                        </Button>
                        <Button
                          id={`cancelEdit_${employee.id}`}
                          data-testid={`cancelEdit_${employee.id}`}
                          value={employee.id}
                          onClick={cancelEdit}
                        >
                          <ClearIcon />
                        </Button>
                      </Grid>
                    </Grid>
                  </ValidatorForm>
                ) : (
                  <Grid container className={styles.employeeInfo} key={index}>
                    <Grid
                      item
                      md={3}
                      id={`name_${index}`}
                      className={styles.employeeName}
                    >
                      {employee.fullName}
                    </Grid>
                    <Grid
                      item
                      md={3}
                      id={`email_${index}`}
                      className={styles.employeeEmail}
                    >
                      {employee.emailAddress}
                    </Grid>

                    <>
                      {!props.isMainViewingSDLR(props.dealers) &&
                      !props.user.isDealerUsedCarsSalesManager ? (
                        <>
                          <Grid item md={2} className={styles.switches}>
                            <MultiToggleWidget
                              settings={{
                                acceptedText: props.t(
                                  "DealerShipDetails.yes"
                                ),
                                rejectedText: props.t(
                                  "DealerShipDetails.no"
                                ),
                                suppClass: `${styles.toggleWrapper}`,
                                toggleLabel: `${styles.activationToggle}`,
                              }}
                              fromDealerTable={true}
                              isMainViewingSDLR=""
                              toggleName={`emailNot_${employee.id}`}
                              currentStatus={
                                employee.receiveEmailsForNotifications
                                  ? "yes"
                                  : "no"
                              }
                              onSelect={(e) =>
                                onSelectEmailNotification(employee, e)
                              }
                            />
                          </Grid>
                          <HasPermissionTo
                            perform={["receivePendingActionColumn"]}
                            permissions={
                              props.user.permissions.rolePermissions
                            }
                            render={() => (
                              <Grid item md={2} className={styles.switches}>
                                <MultiToggleWidget
                                  settings={{
                                    acceptedText: props.t(
                                      "DealerShipDetails.yes"
                                    ),
                                    rejectedText: props.t(
                                      "DealerShipDetails.no"
                                    ),
                                    suppClass: `${styles.toggleWrapper}`,
                                    toggleLabel: `${styles.activationToggle}`,
                                  }}
                                  fromDealerTable={true}
                                  toggleName={`pendingAcNot_${employee.id}`}
                                  currentStatus={
                                    employee.receiveEmailsForPendingActions
                                      ? "yes"
                                      : "no"
                                  }
                                  onSelect={(e) =>
                                    onSelectPendingActionNotification(
                                      employee,
                                      e
                                    )
                                  }
                                />
                              </Grid>
                            )}
                            noRender={() => <Grid item md={2}></Grid>}
                          />
                        </>
                      ) : (
                        <>
                          <Grid
                            item
                            md={2}
                            id={`toggle1_${index}`}
                            className={`${styles.employeeSwitches} switch-3 switch-candy`}
                          >
                            {employee.receiveEmailsForNotifications
                              ? props.t("DealerShipDetails.yes")
                              : props.t("DealerShipDetails.no")}
                          </Grid>
                          <Grid
                            item
                            md={2}
                            id={`toggle2_${index}`}
                            className={`${styles.employeeSwitches} switch-3 switch-candy`}
                          >
                            <HasPermissionTo
                              perform={["receivePendingActionColumn"]}
                              permissions={
                                props.user.permissions.rolePermissions
                              }
                              render={() => (
                                <div>
                                  {employee.receiveEmailsForPendingActions
                                    ? props.t("DealerShipDetails.yes")
                                    : props.t("DealerShipDetails.no")}
                                </div>
                              )}
                            />
                          </Grid>
                        </>
                      )}

                      <HasPermissionTo
                        perform={["dealershipDetails:write"]}
                        permissions={
                          props.user.permissions.rolePermissions
                        }
                        condition={
                          !props.isMainViewingSDLR(props.dealers)
                        }
                        render={() => (
                          <Grid item md={2} className={styles.editDiv}>
                            <Button
                              id={`editEmployee_${employee.id}`}
                              data-testid={`editEmployee_${employee.id}`}
                              value={employee.id}
                              onClick={editExistingEmployee}
                            >
                              <EditIcon />
                            </Button>
                            <Button
                              id={`deleteEmployee_${employee.id}`}
                              data-testid={`deleteEmployee_${employee.id}`}
                              value={employee.id}
                              onClick={deleteExistingEmployee}
                            >
                              <ClearIcon />
                            </Button>
                          </Grid>
                        )}
                      />
                    </>
                  </Grid>
                )}
              </div>
            ))}
          </>
        )}
        {addNewRowActive && (
          <ValidatorForm onSubmit={addNewEmployee}>
            <Grid className={styles.newEmployeeContainer} container>
              <Grid item md={3}>
                <TextValidator
                  style={{ width: "70%" }}
                  name={`inputName_${currentDealer.regionalId}`}
                  value={newEmployee.fullName}
                  variant="outlined"
                  placeholder={props.t("DealerShipDetails.fullName")}
                  onChange={newEmployeeNameOnChange}
                  validators={["required"]}
                  errorMessages={[props.t("Error.requiredField")]}
                />
              </Grid>
              <Grid item md={3}>
                <TextValidator
                  style={{ width: "70%" }}
                  name={`inputEmail_${currentDealer.regionalId}`}
                  value={newEmployee.emailAddress}
                  variant="outlined"
                  placeholder={props.t("DealerShipDetails.emailAddress")}
                  onChange={newEmployeeEmailOnChange}
                  validators={["required", "isEmail"]}
                  errorMessages={[
                    props.t("Error.requiredField"),
                    props.t("Error.invalidInput"),
                  ]}
                />
              </Grid>
              <Grid item md={4} />
              <Grid className={styles.actionDiv} item md={2}>
                <Button
                  id={`submit_${currentDealer.regionalId}`}
                  data-testid={`submit_${currentDealer.regionalId}`}
                  type="submit"
                >
                  <div className={styles.confirm}>OK</div>
                </Button>
                <Button
                  id={`cancelAdd_${currentDealer.regionalId}`}
                  data-testid={`cancelAdd_${currentDealer.regionalId}`}
                  onClick={cancelNewEmployee}
                >
                  <ClearIcon />
                </Button>
              </Grid>
            </Grid>
          </ValidatorForm>
        )}
      </div>
      <HasPermissionTo
        perform={["dealershipDetails:write"]}
        permissions={props.user.permissions.rolePermissions}
        condition={!props.isMainViewingSDLR(props.dealers)}
        render={() => (
          <div
            className={`pagePaddingLeft pagePaddingRight ${styles.editButton}`}
          >
            <Button
              data-testid={`addEmployee_${currentDealer.regionalId}`}
              disabled={
                addNewRowActive || employeeLimitReached
              }
              onClick={showNewEmployeeRow}
            >
              {props.t("DealerShipDetails.addButton")}
            </Button>
            {employeeLimitReached && (
              <div className={styles.maximumEmployee}>
                {props.t("DealerShipDetails.maxEmployee")}
              </div>
            )}
          </div>
        )}
      />
    </>
  );
}

export default withTranslation("emp")(DealerEmployeeTable);
